/**
 * Employee - holds data about a single employee or an extension without an employee attached
 */

import { reactive, computed, toRaw } from 'vue'
import { useSettingsStore } from '@/stores/Settings'
import { useApiStore } from '@/stores/Api'

import { dynamicSort, extendObject } from '@/utilities/Tools'
import { PbxAppEmployeesType, type PbxAppEmployeesCalendar, type PbxAppEmployeesExtension, type PbxAppEmployeesExtensionCallWithDate, type PbxAppEmployeesExtensionWithDate, type PbxAppEmployeesItem, type PbxAppEmployeesPresence, type PbxAppGroupPermissionsResponse, type PbxAppPermissionsResponse } from '@/TcxApi/PbxApp'
import { EmployeePresenceState, type EmployeeViewOtherPartyOverride } from '@/TcxApi/Employee'
import type { CustomerAddress } from '@/TcxApi/Customer'
import { EmployeePresenceOverrideSource, type EmployeePresence } from '@per_moeller/tcx-realtime-client'
import { ExtensionState } from './Employee.types'
import { PbxCallState } from '@/TcxApi/PbxCommon'
import { GlyphIconName } from './GlyphIcons.types'
import { type PbxExtension, type PbxExtensionResponse } from '@/TcxApi/PbxExtension'

const DEFAULT_PERMISSIONS: PbxAppGroupPermissionsResponse = {
	pickup: false, steal: false, spy: false, setPresence: false, queueMember: false, viewOtherParty: false,
	readCalendar: false, writeCalendar: false, readPhonebook: false, writePhonebook: false
}

/**
 * Filter calls across a set of extensions down to the ones relevant to the device I am currently using.
 *
 * A call is included when:
 *  - no device is selected (`deviceId` falsy) — nothing to filter by, show everything, or
 *  - the call carries no `device` tag — fallback for legs without device info (events from older
 *    PBX versions that did not tag mobile legs, or system-originated legs), or
 *  - the call's `device` matches the device I am using.
 *
 * A call is excluded only when it carries a `device` that is NOT the one I am using
 * (e.g. a call ringing my deskphone or mobile while I am paired with the softphone —
 * the PBX tags both SIP and mobile legs with their device id).
 *
 * @param extensions Extensions, each with its list of ongoing calls
 * @param deviceId Id of the device I am currently using (`realPhoneId`), if any
 * @returns The calls to show, sorted by start time
 */
export function filterCallsForDevice(extensions: PbxAppEmployeesExtensionWithDate[], deviceId: string | null | undefined): PbxAppEmployeesExtensionCallWithDate[] {
	const calls: PbxAppEmployeesExtensionCallWithDate[] = []

	for (const ext of extensions) {
		for (const call of ext.calls ?? []) {
			if (!deviceId || !call.device || call.device === deviceId) {
				calls.push(call)
			}
		}
	}
	calls.sort(dynamicSort('started'))
	return calls
}

export default class Employee {
	/** Id of employe or extension */
	_id: string

	/** Type of record: EMPLOYEE or EXTENSION */
	type = PbxAppEmployeesType.EMPLOYEE

	/** Name */
	name = ''

	/** Title (type EMPLOYEE only) */
	title?: string

	/** Department (type EMPLOYEE only) */
	department?: string

	/** Email address (type EMPLOYEE only) */
	emailAddress?: string

	/** Birthdate (type EMPLOYEE only) */
	birthdate?: Date

	/** Id of picture on fileserver (type EMPLOYEE only) */
	picture?: string

	/** Employee override for weather other may see who the employee is talking to (type EMPLOYEE only) */
	viewOtherPartyOverride?: EmployeeViewOtherPartyOverride

	/** Address data (type EMPLOYEE only) */
	address?: CustomerAddress

	/** Presence (type EMPLOYEE only) */
	presence?: PbxAppEmployeesPresence

	/** List of extensions belonging to the employee. For type EXTENSION this holds excatly 1 record. */
	extensions = reactive<PbxAppEmployeesExtensionWithDate[]>([])

	/** Primary extension settings (only set on employee if employee is the user (me === true)) */
	extensionSettings?: PbxExtension

	/** List of calendar events (type EMPLOYEE only) */
	calendar = reactive<PbxAppEmployeesCalendar[]>([])

	/** True if this is the logged on user */
	me = false

	currentOrNextCalendarEvent: EmployeeCurrentEvent | undefined = undefined

	/** @private */
	ss = useSettingsStore()

	/** @private */
	api = useApiStore()

	/**
	 * Create employee instance
	 * @param data Data to initialize the employee with
	 * @param permissions Extension permissions, indexed by extension id
	 */
	constructor(data: PbxAppEmployeesItem, permissions?: PbxAppPermissionsResponse) {
		this._id = data._id
		this.update(data, permissions)
		this.me = this._id === this.ss.auth?.employee
	}

	async loadExtensionSettings() {
		if (!this.me) { return } // failsafe

		try {
			const res = await this.api.get<PbxExtensionResponse>(`/pbx/extension/${this.primaryExtension?._id}?includePhones=true`)

			this.extensionSettings = res.data
		}
		catch (_) { /** Ignore */ }
	}

	/**
	 * Create a clone of the employee (deep copy, only safe way to do it due to reactivity)
	 * @returns A clone of the employee
	 */
	clone() {
		const raw: PbxAppEmployeesItem = {
			_id: this._id,
			type: this.type,
			name: this.name,
			title: this.title,
			department: this.department,
			emailAddress: this.emailAddress,
			birthdate: this.birthdate ? this.birthdate.toISOString() : undefined,
			picture: this.picture,
			viewOtherPartyOverride: this.viewOtherPartyOverride,
			address: structuredClone(toRaw(this.address)),
			presence: structuredClone(toRaw(this.presence)),
			extensions: [],
			calendar: []
		}

		for (const ext of this.extensions) {
			const newExt: PbxAppEmployeesExtensionWithDate = {
				_id: ext._id,
				primary: ext.primary,
				state: ext.state,
				calls: [],
				permissions: structuredClone(toRaw(ext.permissions)),
				mobile: [],
				personalQueue: 0
			}

			for (const call of ext.calls) {
				newExt.calls.push(structuredClone(toRaw(call)))
			}

			for (const mobile of ext.mobile) {
				newExt.mobile.push(structuredClone(toRaw(mobile)))
			}

			if (ext.phones) {
				newExt.phones = []

				for (const phone of ext.phones) {
					newExt.phones.push(structuredClone(toRaw(phone)))
				}
			}

			raw.extensions.push(newExt as unknown as PbxAppEmployeesExtension)
		}

		if (raw.calendar) {
			for (const cal of this.calendar) {
				raw.calendar.push(structuredClone(toRaw(cal)))
			}
		}

		return new Employee(raw)
	}

	/**
	 * Update employee instance
	 * @param data Data to update the employee with
	 * @param permissions Extension permissions, indexed by extension id
	 */
	update(data: PbxAppEmployeesItem, permissions?: PbxAppPermissionsResponse) {
		this.type = data.type
		this.name = data.name
		this.title = data.title
		this.department = data.department
		this.emailAddress = data.emailAddress
		this.birthdate = data.birthdate ? new Date(data.birthdate) : undefined
		this.picture = data.picture
		this.viewOtherPartyOverride = data.viewOtherPartyOverride
		this.address = data.address
		this.presence = data.presence

		// Patch extensions in place. Replacing the array orphans call references that
		// other components have captured, and discards in-memory state (e.g. iOwnHold)
		// that only the realtime CALL_HOLD handler knows about.
		const incoming = data.extensions as PbxAppEmployeesExtensionWithDate[]

		for (const ext of incoming) {
			if (!ext.calls) { ext.calls = [] }

			for (const call of ext.calls) {
				this.fixCallDates(call)
			}
		}

		for (let idx = this.extensions.length - 1; idx >= 0; idx--) {
			if (!incoming.some(ext => ext._id === this.extensions[idx]?._id)) {
				this.extensions.splice(idx, 1)
			}
		}

		for (const ext of incoming) {
			const existing = this.extensions.find(existingExt => existingExt._id === ext._id)
			if (existing) {
				existing.primary = ext.primary
				existing.number = ext.number
				existing.direct = ext.direct
				existing.mobile = ext.mobile
				existing.phones = ext.phones
				existing.personalQueue = ext.personalQueue
				existing.state = ext.state
				existing.redirect = ext.redirect
				if (permissions) {
					existing.permissions = permissions[existing._id] || DEFAULT_PERMISSIONS
				}
				if (!existing.calls) { existing.calls = [] }
				// For the current user, realtime events are the source of truth for calls;
				// only seed from REST when we have none yet. For other users we just
				// replace contents in place (preserving the array reference).
				if (!this.me) {
					existing.calls.splice(0, existing.calls.length, ...ext.calls)
				} else if (existing.calls.length === 0) {
					for (const call of ext.calls) { existing.calls.push(call) }
				}
			} else {
				if (permissions) {
					ext.permissions = permissions[ext._id] || DEFAULT_PERMISSIONS
				}
				this.extensions.push(ext)
			}
		}

		if (data.calendar) {
			for (const event of data.calendar) {
				if (event.start) { event.start = new Date(event.start) }
				if (event.end) { event.end = new Date(event.end) }
				if (event.start.getTime() === event.end.getTime() && !event.isAllDay) {
					event.isAllDay = true
					event.end = new Date(event.end.getTime() + (24 * 60 * 60 * 1000)) // Add one day to end time
				}
				if (event.start.getHours() === 0 && event.start.getMinutes() === 0 && event.end.getHours() === 0 && event.end.getMinutes() === 0) {
					if (daysSinceStartOfYear(event.start) === daysSinceStartOfYear(event.end) - 1) {
						event.isAllDay = true
					}
				}
				if (event.isAllDay && !(event.start.getHours() === 0 && event.start.getMinutes() === 0)) {
					event.start.setHours(0, 0, 0, 0)
				}
				if (event.isAllDay && !(event.end.getHours() === 0 && event.end.getMinutes() === 0)) {
					event.end.setHours(0, 0, 0, 0)
				}
			}
		}
		this.calendar = data.calendar ?? []
	}

	/**
	 * Update employees presence
	 * @param presence Presence to update the employee with
	 */
	updatePresence(presence: EmployeePresence) {
		if (!this.presence) { (this.presence as unknown) = {} } // Failsafe
		if (this.presence) {
			this.presence.state = presence.originalState ?? presence.state
			this.presence.message = presence.originalMessage ?? presence.message
			this.presence.location = presence.location
			this.presence.expire = presence.expire ? new Date(presence.expire) : undefined
			this.presence.overrides = presence.overrides
		}
	}

	/** Get the presence state, adjusted for overrides */
	get presenceState() {
		return computed<EmployeePresenceState>(() => {
			if (!this.presence) { return EmployeePresenceState.AVAILABLE }
			if (this.presence.overrides?.length > 0) {
				return this.presence.overrides[this.presence.overrides.length - 1]?.state as EmployeePresenceState
			} else {
				return this.presence.state
			}
		}).value
	}

	/** Get the presence message, adjusted for overrides */
	get presenceMessage() {
		return computed(() => {
			if (this.presence) {
				if (this.presence?.overrides?.length > 0) {
					const override = this.presence.overrides[this.presence.overrides.length - 1]
					if (override?.source === 'MS365_TEAMS') {
						if (this.presence?.message && this.presence.message.length > 0) {
							return `[Teams] ${this.presence.message}`
						} else {
							return this.ss.auth?.language === 'da' ? '[Teams] møde' : '[Teams] meeting'
						}
					}
				} else {
					return this.presence.message ?? ''
				}
			}
			return ''
		}).value
	}

	/** Does the employee currently have a teams meeting override active */
	get presenceTeamsMeeting() {
		return computed(() => {
			return this.presence?.overrides?.[this.presence.overrides.length - 1]?.source === EmployeePresenceOverrideSource.MS365_TEAMS
		}).value
	}

	/** Does the employee currently have a calendar override active */
	get presenceCalendarEvent() {
		return computed(() => {
			return this.presence?.overrides?.[this.presence.overrides.length - 1]?.source === EmployeePresenceOverrideSource.MS365_CALENDAR
		}).value
	}

	/** Returns the state of the extension(s) */
	get extensionsState() {
		return computed(() => {
			let state = ExtensionState.IDLE

			for (const ext of this.extensions) {
				if (ext.state === 'BUSY') { state = ExtensionState.BUSY }
				else if (ext.state === ExtensionState.RINGING && state === ExtensionState.IDLE) { ext.state = ExtensionState.RINGING }
				else if (ext.state === ExtensionState.CALLING && state === ExtensionState.IDLE) { ext.state = ExtensionState.CALLING }
			}

			return state
		}).value
	}

	/** Returns all active calls - for the current user only the ones relevant to the device being used */
	get calls(): PbxAppEmployeesExtensionCallWithDate[] {
		return filterCallsForDevice(this.extensions, this.me ? this.ss.realPhoneId : null)
	}

	/** Returns the newest answered call */
	get newestAnsweredCall() {
		return computed(() => {
			const calls = this.calls
			if (calls.length === 0) { return null }
			calls.sort(dynamicSort('-started'))
			return calls[0]
		}).value
	}

	get showOtherParty() {
		return computed(() => {
			// employee.currentCall&&employee.currentCallExtension.permissions.viewOtherParty&&employee.viewOtherPartyOverrideCheck
			return this.currentCall && this.currentCallExtension?.permissions.viewOtherParty && !this.currentCall?.hideOtherParty
		}).value
	}

	/** Returns the current call */
	get currentCall() {
		return computed(() => {
			const calls = this.calls

			// If no calls - null
			if (calls.length === 0) { return undefined }

			// If we have an answered call - return the newest one
			calls.sort(dynamicSort('-started'))

			const newestActiveCall = calls.find(call => call.state === PbxCallState.CALL_ANSWER)
			if (newestActiveCall) { return newestActiveCall }

			// If we have a CALLING or RINGING call - return the oldest one
			calls.sort(dynamicSort('started'))

			const oldestCallingCall = calls.find(call => call.state === PbxCallState.CALL_START && call.leg === 'I')
			if (oldestCallingCall) { return oldestCallingCall }

			const oldestRingingCall = calls.find(call => call.state === PbxCallState.CALL_RING)
			if (oldestRingingCall) { return oldestRingingCall }

			// If we have a HELD call - return the newest one
			calls.sort(dynamicSort('-started'))

			const newestHeldCall = calls.find(call => call.state === PbxCallState.CALL_HOLD)
			if (newestHeldCall) { return newestHeldCall }

			// Otherwise return the oldest call
			return calls[0]
		}).value
	}

	/** Returns the extension for the current call */
	get currentCallExtension() {
		return computed(() => {
			const cc = this.currentCall
			if (!cc) { return undefined }

			for (const ext of this.extensions) {
				if (ext.calls.some(call => call?._id === cc?._id)) { return ext }
			}
			return undefined
		}).value
	}

	/** Get current ringing call if any - for the current user only on the device being used */
	get currentRingingCall() {
		return computed(() => {
			return this.calls.find(call => call.state === PbxCallState.CALL_RING)
		}).value
	}

	/** Returns the icon for the current call */
	get currentCallIcon() {
		return computed(() => {
			const cc = this.currentCall
			if (cc) {
				if (cc.state === PbxCallState.CALL_HOLD) { return GlyphIconName['call-pause'] }
				if (cc.leg === 'I') {
					if (cc.state === PbxCallState.CALL_START) { return GlyphIconName['call-dialing'] }
					if (cc.state === PbxCallState.CALL_ANSWER) { return GlyphIconName['call-outgoing'] }
				} else {
					if (cc.state === PbxCallState.CALL_START || cc.state === PbxCallState.CALL_RING) { return GlyphIconName['call-ringing'] }
					if (cc.state === PbxCallState.CALL_ANSWER) { return GlyphIconName['call-incoming'] }
				}
			}
			return GlyphIconName['call-idle']
		}).value
	}

	/** Returns the icon color for the current call */
	get currentCallIconColor() {
		return computed(() => {
			const cc = this.currentCall
			if (!cc) { return 'var(--call-dialing)' }
			if (cc.state === PbxCallState.CALL_RING || (cc.leg === 'O' && cc.state === PbxCallState.CALL_START)) {
				return 'var(--call-ringing)'
			}
			if (cc.leg === 'I') {
				if (cc.state === PbxCallState.CALL_START) { return 'var(--call-dialing)' }
				if (cc.state === PbxCallState.CALL_ANSWER) { return 'var(--call-answered)' }
			}
			return 'var(--call-answered)'
		}).value
	}

	/** Returns the icon for the type of the current call */
	get currentCallTypeIcon() {
		return computed(() => {
			const cc = this.currentCall
			if (cc) {
				if (cc.leg === 'O') {
					if (cc.caller.queueId) { return GlyphIconName['user-group'] }
					if (cc.caller.number.length < 8 && cc.caller.number.length > 0) { return GlyphIconName['user'] }
					if (!cc.caller.number && !cc.caller.name) { return GlyphIconName['blank'] }
					return GlyphIconName['call']
				} else {
					return GlyphIconName['call']
				}
			} else {
				return GlyphIconName['blank']
			}
		}).value
	}

	/** Returns the duration of the current call in seconds */
	get currentCallDuration() {
		return computed(() => {
			const cc = this.currentCall
			if (!cc) { return 0 }
			if (cc.leg === 'I') {
				const val = cc.started ? Math.round((new Date().getTime() - cc.started.getTime()) / 1000) : 0

				if (!isNaN(val) && val >= 0) { return val } else { return 0 }
			} else {
				if (!cc.answered) { return 0 }
				const val = cc.answered ? Math.round((new Date().getTime() - cc.answered.getTime()) / 1000) : 0

				if (!isNaN(val) && val >= 0) { return val } else { return 0 }
			}
		}).value
	}

	/** Returns the primary extension */
	get primaryExtension() {
		return computed(() => {
			let ext = this.extensions.find(ext => ext.primary)
			if (!ext) { ext = this.extensions[0] }
			return ext
		}).value
	}

	/** Get permissions for the employee based on extensions */
	get permissions() {
		return computed(() => {
			const res: PbxAppGroupPermissionsResponse = extendObject({}, DEFAULT_PERMISSIONS)

			for (const ext of this.extensions) {
				const perms = ext.permissions
				if (perms) {
					for (const key in perms) {
						if (perms[key as keyof PbxAppGroupPermissionsResponse]) { res[key as keyof PbxAppGroupPermissionsResponse] = true }
					}
				}
			}
			return res
		}).value
	}

	/** Get the employees initials */
	get initials() {
		return computed(() => {
			const parts = this.name.split(/ +/)
			let out = ''
			if (parts[0]) { out += parts[0][0] }
			if (parts.length > 1 && parts[parts.length - 1]) { out += parts[parts.length - 1]?.[0] }
			return out.toUpperCase()
		}).value
	}

	/** Get the background color for showing the employee presence as a background color */
	get presenceBackgroundColor() {
		return computed(() => {
			if (this.currentCall && this.ss.settings.callBackground) {
				return 'var(--presence-busy-background)'
			} else if (this.presence && this.ss.settings.presenceBackground) {
				return 'var(--presence-' + this.presence.state.replace('_', '-').toLowerCase() + '-background)'
			} else {
				return 'transparent'
			}
		}).value
	}

	/**
	 * Get the current or next calendar event, if any, but only today or tomorrow if now is before 18:00
	 */
	calculateCurrentOrNextCalendarEvent() {
		const now = new Date()
		// Get all events that are not all day and are either in the future or currently ongoing
		const events = this.calendar.filter(event => !event.isAllDay && (event.start >= now || (event.start <= now && event.end >= now)))
		const allDayEvents = this.calendar.filter(event => event.isAllDay && isDateInRange(now, event.start, event.end))
		const eventsRestOfDay = this.calendar.filter(event => !event.isAllDay && event.start.toDateString() === now.toDateString() && event.start > now)

		events.sort(dynamicSort('start'))
		if (events.length === 0 && allDayEvents.length === 0) { return undefined }

		let event: PbxAppEmployeesCalendar | undefined = events[0]

		if (event && event.start > now && allDayEvents.length > 0) {
			event = allDayEvents[0]
		}

		const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)

		if (event && !event.isAllDay && event.start > now) { // If event starts in the future
			if (event.start.toDateString() !== now.toDateString() && event.start.toDateString() !== tomorrow.toDateString()) { event = undefined } // not today and not tomorrow = no event
			else if (event.start.toDateString() === tomorrow.toDateString() && now.getHours() < 18) { event = undefined } // NOSONAR - tomorrow but time now is before 18:00 = no event
		}

		if (eventsRestOfDay.length > 0 && event === eventsRestOfDay[0]) {
			eventsRestOfDay.splice(0, 1) // So that it is not shown twice
		}

		const eventsRestOfDayFormatted = eventsRestOfDay.map(event => {
			return {
				_id: event._id,
				subject: event.subject,
				time: this.calendarPeriodString(event),
				isAllDay: event.isAllDay
			} as EmployeeCurrentEventRestOfDay
		})

		eventsRestOfDayFormatted.sort(dynamicSort('start'))

		if (event) {
			this.currentOrNextCalendarEvent = {
				_id: event._id,
				subject: event.subject,
				time: this.calendarPeriodString(event) ?? '',
				isAllDay: event.isAllDay ?? false,
				restOfDay: eventsRestOfDayFormatted
			}
		} else {
			this.currentOrNextCalendarEvent = undefined
		}
	}

	calendarPeriodString(event: PbxAppEmployeesCalendar) {
		if (event.isAllDay) { return undefined }
		if (event.start.toLocaleTimeString().substring(0, 5) === event.end.toLocaleTimeString().substring(0, 5)) { return undefined } // all day event
		if (event.start.toDateString() !== event.end.toDateString()) { return event.start.toLocaleTimeString().substring(0, 5).replace('.', ':') + ' - ' } // multi day event

		return event.start.toLocaleTimeString().substring(0, 5).replace('.', ':') + ' - ' + event.end.toLocaleTimeString().substring(0, 5).replace('.', ':')
	}

	/**
	 * Lookup a call by channel id
	 * @param channelId Channel id of the call to find
	 */
	getCallFromChannelId(channelId: string) {
		for (const ext of this.extensions) {
			for (const call of ext.calls) {
				if (call.channel === channelId) { return call }
			}
		}
	}

	/**
	 * Lookup a call by session id
	 * @param sessionId Session id of the call to find
	 */
	getCallfromSessionId(sessionId: string) {
		for (const ext of this.extensions) {
			for (const call of ext.calls) {
				if (call.sessionId === sessionId) { return call }
			}
		}
		return undefined
	}

	/**
	 * Convert dates to Date objects
	 */
	fixCallDates(call: PbxAppEmployeesExtensionCallWithDate) {
		if (call.started) { call.started = new Date(call.started) }
		if (call.ringing) { call.ringing = new Date(call.ringing) }
		if (call.answered) { call.answered = new Date(call.answered) }
		if (call.ended) { call.ended = new Date(call.ended) }
		if (call.hold) { call.hold = new Date(call.hold) }
	}
}

// Given a specific date and a start and end date, return true if the date is within the range
function isDateInRange(date: Date, start: Date, end: Date) {
	return date >= start && date <= end
}

function daysSinceStartOfYear(date: Date) {
	const startOfYear = new Date(date.getFullYear(), 0, 1, 0, 0, 0, 0)
	const compareWith = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
	const diffInTime = compareWith.getTime() - startOfYear.getTime()

	return Math.round(diffInTime / (1000 * 60 * 60 * 24))
}

export interface EmployeeCurrentEvent {
	_id: string
	subject: string | undefined
	time: string
	isAllDay: boolean
	restOfDay: EmployeeCurrentEventRestOfDay[]
}

export interface EmployeeCurrentEventRestOfDay {
	_id: string
	subject: string | undefined
	time: string
	isAllDay: boolean
}
